/**
 * The protobuf wire format, read by hand.
 *
 * The reader has no general protobuf deserialization support (see serde.ts).
 * The few Foxglove schemas it does decode are each a fixed handful of fields,
 * so rather than pull in a protobuf runtime and schema compiler for them, each
 * gets a small decoder over this reader.
 */

// Plain const object (not an enum): wire types are parsed from arbitrary
// message bytes, so comparisons happen against plain numbers.
export const WireType = {
  Varint: 0,
  Fixed64: 1,
  LengthDelimited: 2,
  Fixed32: 5,
} as const;

export class WireReader {
  #data: Uint8Array;
  #view: DataView;
  #offset = 0;

  constructor(data: Uint8Array) {
    this.#data = data;
    this.#view = new DataView(data.buffer, data.byteOffset, data.byteLength);
  }

  public get atEnd(): boolean {
    return this.#offset >= this.#data.length;
  }

  public varint(): number {
    let value = 0;
    let shift = 0;
    for (;;) {
      if (this.#offset >= this.#data.length) {
        throw new Error("Truncated protobuf message: unterminated varint");
      }
      const byte = this.#data[this.#offset++];
      value += (byte & 0x7f) * 2 ** shift;
      if ((byte & 0x80) === 0) {
        return value;
      }
      shift += 7;
    }
  }

  public bytes(length: number): Uint8Array {
    if (this.#offset + length > this.#data.length) {
      throw new Error(
        `Truncated protobuf message: ${length} bytes declared, ${this.#data.length - this.#offset} available`,
      );
    }
    const view = this.#data.subarray(this.#offset, this.#offset + length);
    this.#offset += length;
    return view;
  }

  /** A `fixed32`/`uint32` stored as four little-endian bytes (wire type 5). */
  public fixed32(): number {
    this.#ensure(4, "fixed32");
    const value = this.#view.getUint32(this.#offset, true);
    this.#offset += 4;
    return value;
  }

  /** A `double` stored as eight little-endian bytes (wire type 1). */
  public double(): number {
    this.#ensure(8, "double");
    const value = this.#view.getFloat64(this.#offset, true);
    this.#offset += 8;
    return value;
  }

  public skip(wireType: number): void {
    switch (wireType) {
      case WireType.Varint:
        this.varint();
        return;
      case WireType.Fixed64:
        this.bytes(8);
        return;
      case WireType.LengthDelimited:
        this.bytes(this.varint());
        return;
      case WireType.Fixed32:
        this.bytes(4);
        return;
      default:
        throw new Error(`Unsupported protobuf wire type ${wireType}`);
    }
  }

  #ensure(length: number, what: string): void {
    if (this.#offset + length > this.#data.length) {
      throw new Error(
        `Truncated protobuf message: ${what} needs ${length} bytes, ${this.#data.length - this.#offset} available`,
      );
    }
  }
}

/** A `google.protobuf.Timestamp`: field 1 seconds, field 2 nanos, both varints. */
export function decodeTimestamp(data: Uint8Array): {
  sec: number;
  nsec: number;
} {
  const reader = new WireReader(data);
  const timestamp = { sec: 0, nsec: 0 };
  while (!reader.atEnd) {
    const tag = reader.varint();
    const fieldNumber = tag >>> 3;
    const wireType = tag & 0x07;
    if (fieldNumber === 1 && wireType === WireType.Varint) {
      timestamp.sec = reader.varint();
    } else if (fieldNumber === 2 && wireType === WireType.Varint) {
      timestamp.nsec = reader.varint();
    } else {
      reader.skip(wireType);
    }
  }
  return timestamp;
}
